import type { Locale } from '@/i18n/locales';
import { endpoints } from '@/api/endpoints';
import { isApiRequestError } from '@/api/client';
import type { LoginInput, RegisterInput, Me, TelegramAuthData, TelegramAuthResult } from '@tg/shared';
import { useSession, type Prefs } from './session';
import { toast } from './toasts';
import { reconnectSocket } from '@/socket/socket';
import { useDailyBonus } from '@/features/bonus/useDailyBonus';

/** GET /api/me → user | guest. API down → guest + apiAvailable=false (lobby shows mocks). */
export async function bootstrapSession(): Promise<void> {
  const s = useSession.getState();
  try {
    const me = await endpoints.me();
    s.setApiAvailable(true);
    s.setMe(me);
  } catch (e) {
    if (isApiRequestError(e) && !e.isNetwork) s.setApiAvailable(true);
    else s.setApiAvailable(false);
    s.setMe(null);
  }
}

export async function login(input: LoginInput): Promise<Me> {
  const { me, deletionCancelled } = await endpoints.login(input);
  // signing in within the 7 days cancels a pending account deletion (server side): say so
  if (deletionCancelled) toast.success('profile.deletion.cancelledBySignIn');
  useSession.getState().setApiAvailable(true);
  useSession.getState().setMe(me);
  afterAuthChange();
  return me;
}

export async function register(input: RegisterInput): Promise<Me> {
  const me = await endpoints.register(input);
  useSession.getState().setApiAvailable(true);
  useSession.getState().setMe(me);
  afterAuthChange();
  return me;
}

/** Telegram Login Widget → session; `created`: a new account (the caller suggests changing the nickname) */
export async function signInWithTelegram(auth: TelegramAuthData, locale: Locale): Promise<{ me: Me; created: boolean }> {
  const { me, created, deletionCancelled } = (await endpoints.telegramAuth({ auth, mode: 'login', locale })) as TelegramAuthResult;
  if (deletionCancelled) toast.success('profile.deletion.cancelledBySignIn');
  useSession.getState().setApiAvailable(true);
  useSession.getState().setMe(me);
  afterAuthChange();
  return { me, created };
}

export async function logout(): Promise<void> {
  try {
    await endpoints.logout();
  } finally {
    useSession.getState().setMe(null);
    afterAuthChange();
  }
}

/**
 * "Sign out on all devices". Unlike `logout`, a failure (network, rate limit) is thrown and the local session
 * is kept — the other devices were not signed out. UNAUTHORIZED means this session is already gone.
 */
export async function logoutAll(): Promise<void> {
  try {
    await endpoints.logoutAll();
  } catch (e) {
    if (!(isApiRequestError(e) && e.code === 'UNAUTHORIZED')) throw e;
  }
  useSession.getState().setMe(null);
  afterAuthChange();
}

/** Session cookie changed: rebind the socket, reload per-user data. */
function afterAuthChange(): void {
  reconnectSocket();
  useDailyBonus.getState().reset();
  void useDailyBonus.getState().load();
}

/** Change preferences locally and, for signed-in players, in the profile. */
export function updatePrefs(p: Partial<Prefs>): void {
  const s = useSession.getState();
  s.setPrefs(p);
  if (s.status === 'user') {
    endpoints.settings(p).catch((e: unknown) => toast.error(e));
  }
}

export function persistLocale(l: Locale): void {
  const s = useSession.getState();
  if (s.locale === l && s.me?.locale === l) return;
  s.setLocale(l);
  if (s.status === 'user' && s.me && s.me.locale !== l) {
    useSession.setState({ me: { ...s.me, locale: l } });
    endpoints.settings({ locale: l }).catch((e: unknown) => toast.error(e));
  }
}
